/* eslint-disable lingui/text-restrictions */
/* eslint-disable lingui/no-unlocalized-strings */

import { t } from "@lingui/macro";
import { ArrowRight } from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import { useAuthStore } from "@/client/stores/auth";

const matchedKeywords = [
  "React",
  "TypeScript",
  "Node.js",
  "REST APIs",
  "Git",
  "Agile",
  "CI/CD",
  "PostgreSQL",
];

const missingKeywords = ["Kubernetes", "GraphQL", "AWS", "Terraform"];

export const FeatureJobMatchSection = () => {
  const isLoggedIn = useAuthStore((s) => !!s.user);

  return (
    <section id="job-match" className="relative py-24 sm:py-32">
      <div className="container">
        <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Left: screenshot */}
          <motion.div
            className="flex justify-center"
            viewport={{ once: true }}
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0, transition: { duration: 0.6 } }}
          >
            <img
              src="/screenshots/job-matching.png"
              alt="Job Description Matching showing 100% keyword match with 11 matched keywords and tailoring suggestions"
              loading="lazy"
              decoding="async"
              className="max-w-xs rounded-xl shadow-2xl ring-1 ring-foreground/10 sm:max-w-sm"
            />
          </motion.div>

          {/* Right: content */}
          <motion.div
            className="space-y-6"
            viewport={{ once: true }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0, transition: { delay: 0.1 } }}
          >
            <p className="text-sm font-semibold uppercase tracking-wider text-purple-600">
              {t`Smart Matching`}
            </p>
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
              {t`Match Your Resume to Any Job Description`}
            </h2>
            <p className="text-lg leading-relaxed text-foreground/60">
              Paste any job posting and instantly see how well your resume aligns. CareerBldr's AI
              identifies exactly which keywords you're hitting and which you're missing, so you can
              tailor every application instead of guessing.
            </p>

            {/* Live keyword demo */}
            <div className="space-y-4 rounded-xl border border-border/50 bg-background p-5">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">Keyword Analysis</span>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-purple-600">78%</span>
                  <span className="text-xs text-foreground/40">match</span>
                </div>
              </div>

              <div>
                <p className="mb-2 text-xs font-medium text-green-600">
                  Matched ({matchedKeywords.length})
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {matchedKeywords.map((kw) => (
                    <span
                      key={kw}
                      className="rounded-full bg-green-500/10 px-2.5 py-1 text-xs font-medium text-green-700 dark:text-green-400"
                    >
                      {kw}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-2 text-xs font-medium text-red-500">
                  Missing ({missingKeywords.length})
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {missingKeywords.map((kw) => (
                    <span
                      key={kw}
                      className="rounded-full bg-red-500/10 px-2.5 py-1 text-xs font-medium text-red-600 dark:text-red-400"
                    >
                      {kw}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <ul className="space-y-2.5 text-sm text-foreground/70">
              {[
                "Paste any job posting for instant analysis",
                "See matched vs. missing keywords at a glance",
                "Get targeted suggestions to close each gap",
                "Auto-tailoring adjusts your resume to the job description automatically",
              ].map((bullet, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <svg
                    className="mt-0.5 h-4 w-4 shrink-0 text-green-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {bullet}
                </li>
              ))}
            </ul>

            <Button asChild size="lg">
              <Link to={isLoggedIn ? "/dashboard" : "/auth/register"}>
                {isLoggedIn ? t`Match Your Resume` : t`Try Job Matching`}
                <ArrowRight className="ml-2" weight="bold" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
